/**
 * FORMA Studio — Mock Gen v1.0
 * AI Vehicle Mockup Generator
 *
 * Base: Swap Engine prototype (fork + reskin by STUDIO)
 * Visual: FORMA Design System (tokens v1.0 + app-shell)
 * Integration: FORGE — AuthProvider, exports, logo, deploy
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  AppStep,
  GraphicPanel,
  VehicleDetails,
  ScenarioMood,
  MockupResult,
  PanelType,
  SavedProject,
} from './types';
import { SCENARIOS, VIEWS } from './constants';
import { generateCarMockup } from './services/geminiService';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { useExports } from './hooks/useExports';
import { AuthModal } from './components/AuthModal';
import { LockScreen } from './components/LockScreen';
import logoFormaStudio from './assets/FORMA-Studio_logo_forma_icon_w.png';

// ─── Types estesi per MockGen ────────────────────────────────
export interface CarPhoto {
  angle: '3/4 Front' | '3/4 Rear' | 'Lateral';
  previewUrl: string;
  base64: string;
}

export interface BrandingData {
  brand: string;
  model: string;
  year: string;
  style: string;
  logoPreview?: string;
  logoBase64?: string;
}

// ─── Costanti MockGen ────────────────────────────────────────
const PANEL_TYPES: PanelType[] = [
  'Side Left', 'Side Right', 'Hood', 'Roof', 'Trunk', 'Front Bumper', 'Rear Bumper',
];
const MAIN_PANELS: PanelType[]     = ['Side Left', 'Side Right', 'Hood'];
const OPTIONAL_PANELS: PanelType[] = ['Roof', 'Trunk', 'Front Bumper', 'Rear Bumper'];

const CAR_ANGLES: CarPhoto['angle'][] = ['3/4 Front', '3/4 Rear', 'Lateral'];
const ANGLE_META: Record<CarPhoto['angle'], { label: string; hint: string }> = {
  '3/4 Front': { label: '3/4 Anteriore', hint: 'Fronte + lato sinistro' },
  '3/4 Rear':  { label: '3/4 Posteriore', hint: 'Retro + lato sinistro' },
  'Lateral':   { label: 'Laterale', hint: 'Profilo completo lato sinistro' },
};

const STEP_UI = [
  { step: AppStep.Upload,     label: 'Panels'   },
  { step: AppStep.Vehicle,    label: 'Car'      },
  { step: AppStep.Scenario,   label: 'Scenario' },
  { step: AppStep.Generation, label: 'Branding' },
  { step: AppStep.Results,    label: 'Results'  },
];

// ─── Utility: Download con watermark ────────────────────────
const downloadWithWatermark = (imageUrl: string, filename: string): void => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const canvas  = document.createElement('canvas');
    canvas.width  = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0);
    const fontSize = Math.max(13, Math.round(img.width * 0.017));
    const pad      = Math.round(fontSize * 1.2);
    ctx.font      = `500 ${fontSize}px "Space Grotesk", sans-serif`;
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.fillText('Mockup Gen by FORMA Studio', img.width - pad, img.height - pad);
    const a    = document.createElement('a');
    a.download = filename;
    a.href     = canvas.toDataURL('image/png');
    a.click();
  };
  img.src = imageUrl;
};

// ════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════════════════════

// ─── Header ─────────────────────────────────────────────────
const AppHeader = ({
  onGalleryClick,
  onNewMockup,
  currentStep,
  onOpenAuth,
}: {
  onGalleryClick: () => void;
  onNewMockup:    () => void;
  currentStep:    AppStep;
  onOpenAuth:     () => void;
}) => {
  const { session } = useAuth();
  const exports = useExports();
  const isLoggedIn = !!session;

  const userInitials = session?.user?.displayName
    ? session.user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'CR';

  return (
    <header className="fs-app-header">
      <div className="fs-app-header__inner">

        {/* LEFT: logo + divider + app name */}
        <div className="fs-app-header__left">
          <a href="https://forma.frankriccidesign.com" className="fs-app-header__logo">
            <img
              src={logoFormaStudio}
              alt="FORMA Studio"
              className="fs-app-header__logo-icon"
              style={{ height: 28, width: 'auto' }}
            />
            <span className="fs-app-header__logo-name">FORMA Studio</span>
            <span className="fs-badge-beta-header">BETA</span>
          </a>
          <span className="fs-app-header__divider" aria-hidden="true" />
          <span className="fs-app-header__app-name">Mock Gen</span>
        </div>

        {/* CENTER: action buttons */}
        <div className="fs-app-header__actions">
          <button
            onClick={onNewMockup}
            className="fs-btn-ghost"
            style={{ padding: '7px 14px', fontSize: 'var(--fs-text-xs)' }}
          >
            New Mockup
          </button>
          <button
            onClick={onGalleryClick}
            className={currentStep === AppStep.Gallery ? 'fs-btn-primary' : 'fs-btn-ghost'}
            style={{ padding: '7px 14px', fontSize: 'var(--fs-text-xs)' }}
          >
            Gallery
          </button>
        </div>

        {/* RIGHT: auth area */}
        <div className="fs-app-header__right">
          <a href="https://forma.frankriccidesign.com" className="fs-app-header__back-link">
            &larr; Portal
          </a>
          {!isLoggedIn ? (
            <button className="fs-btn-primary fs-app-header__cta-login" onClick={onOpenAuth}>
              Sign in
            </button>
          ) : (
            <div className="fs-app-header__account">
              <div className="fs-app-header__export-counter">
                <span className="fs-app-header__export-used">{exports.exportsUsed}</span>
                <span className="fs-app-header__export-sep">/</span>
                <span className="fs-app-header__export-total">{exports.exportsMax}</span>
              </div>
              <div className="fs-app-header__avatar">{userInitials}</div>
            </div>
          )}
        </div>

      </div>
    </header>
  );
};

// ─── StepIndicator ──────────────────────────────────────────
const StepIndicator = ({ currentStep }: { currentStep: AppStep }) => {
  if (currentStep === AppStep.Gallery) return null;
  return (
    <div className="flex justify-between items-center px-4 py-8 max-w-xl mx-auto w-full">
      {STEP_UI.map(({ step, label }, i) => {
        const completed = currentStep > step;
        const active    = currentStep === step;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                style={{
                  background:  completed ? 'var(--fs-cyan)' : active ? 'transparent' : 'transparent',
                  color:       completed ? 'var(--fs-on-cyan)' : active ? 'var(--fs-cyan)' : 'var(--fs-text-disabled)',
                  border:      completed ? 'none' : active ? '2px solid var(--fs-cyan)' : '1px solid var(--fs-border)',
                  boxShadow:   active ? '0 0 12px rgba(0,212,255,0.25)' : 'none',
                }}
              >
                {completed ? '\u2713' : i + 1}
              </div>
              <span
                className="text-[9px] uppercase tracking-widest"
                style={{ color: active ? 'var(--fs-text)' : completed ? 'var(--fs-text-muted)' : 'var(--fs-text-disabled)' }}
              >
                {label}
              </span>
            </div>
            {i < STEP_UI.length - 1 && (
              <div
                className="h-px flex-1 mx-2 transition-colors"
                style={{ background: completed ? 'var(--fs-cyan)' : 'var(--fs-border)' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── CropModal ──────────────────────────────────────────────
const CropModal = ({
  panel,
  onSave,
  onClose,
}: {
  panel:   GraphicPanel;
  onSave:  (p: GraphicPanel) => void;
  onClose: () => void;
}) => {
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const imgRef      = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [crop, setCrop] = useState({ x: 10, y: 10, width: 80, height: 80 });

  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

  const startDrag = (e: React.MouseEvent, mode: string) => {
    e.preventDefault();
    e.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    const rect   = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const start  = { ...crop };

    const onMove = (mv: MouseEvent) => {
      const dx = ((mv.clientX - startX) / rect.width)  * 100;
      const dy = ((mv.clientY - startY) / rect.height) * 100;

      setCrop(() => {
        let { x, y, width, height } = start;

        if (mode === 'move') {
          x = clamp(x + dx, 0, 100 - width);
          y = clamp(y + dy, 0, 100 - height);
        } else {
          if (mode.includes('n')) { y = clamp(y + dy, 0, y + height - 5); height = start.y + start.height - y; }
          if (mode.includes('s')) { height = clamp(height + dy, 5, 100 - y); }
          if (mode.includes('w')) { x = clamp(x + dx, 0, x + width - 5); width = start.x + start.width - x; }
          if (mode.includes('e')) { width = clamp(width + dx, 5, 100 - x); }
        }

        return { x, y, width, height };
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const sX = (crop.x / 100) * img.naturalWidth;
    const sY = (crop.y / 100) * img.naturalHeight;
    const sW = (crop.width  / 100) * img.naturalWidth;
    const sH = (crop.height / 100) * img.naturalHeight;
    canvas.width  = sW;
    canvas.height = sH;
    ctx.drawImage(img, sX, sY, sW, sH, 0, 0, sW, sH);
    const b64Full  = canvas.toDataURL('image/png');
    const b64      = b64Full.split(',')[1];
    const binary   = atob(b64);
    const arr      = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
    const blob = new Blob([arr], { type: 'image/png' });
    onSave({ ...panel, previewUrl: b64Full, base64: b64, file: blob });
  };

  const handleStyle: Record<string, React.CSSProperties> = {
    n:  { top: -4, left: '20%', right: '20%', height: 8, cursor: 'ns-resize' },
    s:  { bottom: -4, left: '20%', right: '20%', height: 8, cursor: 'ns-resize' },
    w:  { left: -4, top: '20%', bottom: '20%', width: 8, cursor: 'ew-resize' },
    e:  { right: -4, top: '20%', bottom: '20%', width: 8, cursor: 'ew-resize' },
    nw: { top: -5, left: -5, width: 10, height: 10, cursor: 'nwse-resize', borderRadius: '50%', background: 'var(--fs-cyan)' },
    ne: { top: -5, right: -5, width: 10, height: 10, cursor: 'nesw-resize', borderRadius: '50%', background: 'var(--fs-cyan)' },
    sw: { bottom: -5, left: -5, width: 10, height: 10, cursor: 'nesw-resize', borderRadius: '50%', background: 'var(--fs-cyan)' },
    se: { bottom: -5, right: -5, width: 10, height: 10, cursor: 'nwse-resize', borderRadius: '50%', background: 'var(--fs-cyan)' },
  };

  return (
    <div className="fs-modal-backdrop">
      <div className="fs-modal" style={{ maxWidth: 560 }}>
        <div className="flex justify-between items-center mb-6">
          <h3
            className="font-bold"
            style={{
              fontFamily: 'var(--fs-font-headline)',
              fontSize:   'var(--fs-text-xl)',
              color:      'var(--fs-text)',
              letterSpacing: 'var(--fs-tracking-tight)',
            }}
          >
            Adjust Panel
          </h3>
          <button className="fs-modal__close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div
          ref={containerRef}
          className="relative overflow-hidden mb-6"
          style={{
            background:   'var(--fs-surface-low)',
            border:       '1px solid var(--fs-border-soft)',
            borderRadius: 'var(--fs-radius-card)',
          }}
        >
          <img ref={imgRef} src={panel.previewUrl} className="w-full h-auto block select-none" draggable={false} alt="To crop" />
          <div
            className="absolute"
            style={{
              left:      `${crop.x}%`,
              top:       `${crop.y}%`,
              width:     `${crop.width}%`,
              height:    `${crop.height}%`,
              border:    '2px solid var(--fs-cyan)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
              cursor:    'move',
            }}
            onMouseDown={(e) => startDrag(e, 'move')}
          >
            {/* Rule of thirds grid */}
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
              {[...Array(9)].map((_, i) => (
                <div key={i} style={{ border: '0.5px solid rgba(0,212,255,0.2)' }} />
              ))}
            </div>
            {/* 8 resize handles: 4 edges + 4 corners */}
            {Object.entries(handleStyle).map(([mode, style]) => (
              <div
                key={mode}
                className="absolute"
                style={{ ...style, position: 'absolute', zIndex: 2 }}
                onMouseDown={(e) => startDrag(e, mode)}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button className="fs-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="fs-btn-primary" onClick={handleSave}>Apply Crop</button>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

// ─── PanelCard ──────────────────────────────────────────────
const PanelCard = ({
  type,
  panels,
  onFileUpload,
  onPaste,
  onUpdateType,
  onEdit,
  onRemove,
}: {
  type:         PanelType;
  panels:       GraphicPanel[];
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, slot: PanelType) => void | Promise<void>;
  onPaste:      (blob: Blob, slot: PanelType) => void | Promise<void>;
  onUpdateType: (id: string, type: PanelType) => void;
  onEdit:       (p: GraphicPanel) => void;
  onRemove:     (id: string) => void;
}) => {
  const p = panels.find(panel => panel.type === type);
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) onPaste(blob, type);
        return;
      }
    }
  };
  return (
    <div
      className="relative group flex flex-col overflow-hidden aspect-square transition-all"
      style={{
        background:   p ? 'var(--fs-surface-low)' : 'var(--fs-surface)',
        border:       p ? '1px solid var(--fs-border-soft)' : '1px dashed var(--fs-border)',
        borderRadius: 'var(--fs-radius-card)',
      }}
    >
      {p ? (
        <>
          <div className="flex-1 relative overflow-hidden" style={{ background: 'var(--fs-bg)' }}>
            <img src={p.previewUrl} className="w-full h-full object-contain" alt={type} />
          </div>
          <div className="p-3 flex flex-col gap-2" style={{ borderTop: '1px solid var(--fs-border)' }}>
            <span className="text-[9px] uppercase tracking-widest px-1" style={{ color: 'var(--fs-text-disabled)' }}>
              {type}
            </span>
            <select
              value={p.type}
              onChange={(e) => onUpdateType(p.id, e.target.value as PanelType)}
              className="w-full outline-none appearance-none px-2 py-1.5 text-[10px] uppercase tracking-wider cursor-pointer transition-all"
              style={{
                background:   'var(--fs-surface-mid)',
                border:       '1px solid var(--fs-border)',
                borderRadius: 'var(--fs-radius-sm)',
                color:        'var(--fs-text-muted)',
              }}
            >
              {PANEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="flex gap-2">
              <button
                onClick={() => onEdit(p)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[9px] uppercase tracking-widest transition-all"
                style={{
                  background:   'var(--fs-surface-mid)',
                  border:       '1px solid var(--fs-border)',
                  borderRadius: 'var(--fs-radius-sm)',
                  color:        'var(--fs-text-muted)',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--fs-text)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--fs-text-muted)')}
              >
                Adjust
              </button>
              <button
                onClick={() => onRemove(p.id)}
                className="px-3 py-1.5 text-[10px] transition-all"
                style={{
                  background:   'var(--fs-surface-mid)',
                  border:       '1px solid var(--fs-border)',
                  borderRadius: 'var(--fs-radius-sm)',
                  color:        'var(--fs-text-disabled)',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#EF4444')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--fs-text-disabled)')}
              >
                &#10005;
              </button>
            </div>
          </div>
        </>
      ) : (
        <label
          tabIndex={0}
          onPaste={handlePaste}
          className="flex-1 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all p-4 text-center outline-none"
          style={{ color: 'var(--fs-text-disabled)' }}
          onMouseEnter={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-cyan)';
            el.style.background  = 'var(--fs-cyan-dim)';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-border)';
            el.style.background  = 'var(--fs-surface)';
          }}
          onFocus={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-cyan)';
            el.style.background  = 'var(--fs-cyan-dim)';
          }}
          onBlur={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-border)';
            el.style.background  = 'var(--fs-surface)';
          }}
        >
          <input type="file" className="hidden" accept="image/*" onChange={(e) => onFileUpload(e, type)} />
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
            style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid var(--fs-border)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--fs-text-disabled)" strokeWidth="1.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <div className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest">{type}</span>
            <span className="block text-[8px]" style={{ color: 'rgba(107,114,128,0.6)' }}>Click or Ctrl+V</span>
          </div>
        </label>
      )}
    </div>
  );
};

// ─── CarPhotoSlot ────────────────────────────────────────────
const CarPhotoSlot = ({
  angle,
  photo,
  onUpload,
  onPaste,
  onRemove,
}: {
  angle:    CarPhoto['angle'];
  photo:    CarPhoto | undefined;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>, angle: CarPhoto['angle']) => void;
  onPaste:  (blob: Blob, angle: CarPhoto['angle']) => void | Promise<void>;
  onRemove: (angle: CarPhoto['angle']) => void;
}) => {
  const meta = ANGLE_META[angle];
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) onPaste(blob, angle);
        return;
      }
    }
  };
  return (
    <div
      className="flex flex-col overflow-hidden transition-all"
      style={{
        background:   photo ? 'var(--fs-surface-low)' : 'var(--fs-surface)',
        border:       photo ? '1px solid var(--fs-border-soft)' : '1px dashed var(--fs-border)',
        borderRadius: 'var(--fs-radius-card)',
        minHeight:    180,
      }}
    >
      {photo ? (
        <>
          <div className="relative flex-1 overflow-hidden aspect-video">
            <img src={photo.previewUrl} className="w-full h-full object-cover" alt={angle} />
            <button
              onClick={() => onRemove(angle)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-xs transition-colors"
              style={{
                background:   'rgba(10,10,10,0.75)',
                border:       '1px solid var(--fs-border)',
                borderRadius: 'var(--fs-radius-md)',
                color:        'var(--fs-text-disabled)',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#EF4444')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--fs-text-disabled)')}
            >
              &#10005;
            </button>
          </div>
          <div className="px-3 py-2">
            <span
              className="text-[9px] uppercase tracking-widest"
              style={{ color: 'var(--fs-cyan)', fontFamily: 'var(--fs-font-headline)' }}
            >
              &#10003; {meta.label}
            </span>
          </div>
        </>
      ) : (
        <label
          tabIndex={0}
          onPaste={handlePaste}
          className="flex-1 flex flex-col items-center justify-center gap-3 cursor-pointer p-5 text-center transition-all group outline-none"
          onMouseEnter={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-cyan)';
            el.style.background  = 'var(--fs-cyan-dim)';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-border)';
            el.style.background  = 'var(--fs-surface)';
          }}
          onFocus={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-cyan)';
            el.style.background  = 'var(--fs-cyan-dim)';
          }}
          onBlur={e => {
            const el = e.currentTarget.parentElement!;
            el.style.borderColor = 'var(--fs-border)';
            el.style.background  = 'var(--fs-surface)';
          }}
        >
          <input type="file" className="hidden" accept="image/*" onChange={(e) => onUpload(e, angle)} />
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid var(--fs-border)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--fs-text-disabled)" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <div className="space-y-1">
            <span className="block text-sm font-semibold" style={{ fontFamily: 'var(--fs-font-body)', color: 'var(--fs-text-muted)' }}>
              {meta.label}
            </span>
            <span className="block text-[10px]" style={{ color: 'var(--fs-text-disabled)' }}>Click or Ctrl+V</span>
          </div>
        </label>
      )}
    </div>
  );
};

// ─── SectionHeading ─────────────────────────────────────────
const SectionHeading = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div className="text-center space-y-2 mb-8">
    <h2
      style={{
        fontFamily:    'var(--fs-font-headline)',
        fontWeight:    'var(--fs-weight-bold)',
        fontSize:      'var(--fs-text-3xl)',
        color:         'var(--fs-text)',
        letterSpacing: 'var(--fs-tracking-tight)',
        lineHeight:    1.15,
      }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        style={{
          fontFamily: 'var(--fs-font-body)',
          fontSize:   'var(--fs-text-sm)',
          color:      'var(--fs-text-muted)',
          maxWidth:   480,
          margin:     '0 auto',
        }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

// ─── NavActions ──────────────────────────────────────────────
const NavActions = ({
  onBack,
  onNext,
  nextLabel   = 'Next Step',
  backLabel   = 'Back',
  nextDisabled = false,
  nextStyle,
}: {
  onBack?:      () => void;
  onNext:       () => void;
  nextLabel?:   string;
  backLabel?:   string;
  nextDisabled?: boolean;
  nextStyle?:   React.CSSProperties;
}) => (
  <div className="flex justify-between items-center pt-10">
    {onBack ? (
      <button onClick={onBack} className="fs-btn-text text-[10px] uppercase tracking-widest">
        &larr; {backLabel}
      </button>
    ) : <div />}
    <button
      className="fs-btn-primary"
      disabled={nextDisabled}
      onClick={onNext}
      style={{
        padding: '12px 32px',
        opacity: nextDisabled ? 0.35 : 1,
        cursor:  nextDisabled ? 'not-allowed' : 'pointer',
        ...nextStyle,
      }}
    >
      {nextLabel}
    </button>
  </div>
);

// ════════════════════════════════════════════════════════════
// MAIN APP CONTENT
// ════════════════════════════════════════════════════════════
function MockGenContent() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const openAuth = () => setIsAuthModalOpen(true);
  const closeAuth = () => setIsAuthModalOpen(false);

  // ─── State ──────────────────────────────────────────────
  const [step, setStep]             = useState<AppStep>(AppStep.Upload);
  const [panels, setPanels]         = useState<GraphicPanel[]>([]);
  const [carPhotos, setCarPhotos]   = useState<CarPhoto[]>([]);
  const [instructions, setInstructions] = useState('');
  const [scenarioId, setScenarioId] = useState<ScenarioMood>('Studio');
  const [branding, setBranding]     = useState<BrandingData>({
    brand: '', model: '', year: '', style: '',
  });
  const [showOptional, setShowOptional]     = useState(false);
  const [editingPanel, setEditingPanel]     = useState<GraphicPanel | null>(null);
  const [isGenerating, setIsGenerating]     = useState(false);
  const [currentGenView, setCurrentGenView] = useState('');
  const [results, setResults]               = useState<MockupResult[]>([]);
  const [error, setError]                   = useState<string | null>(null);
  const [gallery, setGallery]               = useState<SavedProject[]>([]);
  const [isSavedInSession, setIsSavedInSession] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('mockgen_gallery');
    if (stored) setGallery(JSON.parse(stored));
  }, []);

  // ─── File handlers ──────────────────────────────────────
  const handlePanelUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    slotType?: PanelType
  ) => {
    const files = Array.from(e.target.files || []) as File[];
    const newPanels: GraphicPanel[] = await Promise.all(
      files.map(async (file) => {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(file);
        });
        let assigned: PanelType = slotType || 'Side Left';
        if (!slotType) {
          const used = panels.map(p => p.type);
          assigned = PANEL_TYPES.find(t => !used.includes(t)) || 'Side Left';
        }
        return {
          id: Math.random().toString(36).substr(2, 9),
          file,
          previewUrl: URL.createObjectURL(file),
          type: assigned,
          base64,
        };
      })
    );
    if (slotType) {
      setPanels(prev => [...prev.filter(p => p.type !== slotType), ...newPanels.slice(0, 1)]);
    } else {
      setPanels(prev => [...prev, ...newPanels].slice(0, 7));
    }
    e.target.value = '';
  };

  const handleCarPhotoUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    angle: CarPhoto['angle']
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(file);
    });
    const photo: CarPhoto = { angle, previewUrl: URL.createObjectURL(file), base64 };
    setCarPhotos(prev => [...prev.filter(p => p.angle !== angle), photo]);
    e.target.value = '';
  };

  const handlePanelPaste = async (blob: Blob, slotType: PanelType) => {
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(blob);
    });
    const panel: GraphicPanel = {
      id: Math.random().toString(36).substr(2, 9),
      file: blob as File,
      previewUrl: URL.createObjectURL(blob),
      type: slotType,
      base64,
    };
    setPanels(prev => [...prev.filter(p => p.type !== slotType), panel]);
  };

  const handleCarPhotoPaste = async (blob: Blob, angle: CarPhoto['angle']) => {
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(blob);
    });
    const photo: CarPhoto = { angle, previewUrl: URL.createObjectURL(blob), base64 };
    setCarPhotos(prev => [...prev.filter(p => p.angle !== angle), photo]);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
    setBranding(prev => ({ ...prev, logoPreview: base64, logoBase64: base64.split(',')[1] }));
    e.target.value = '';
  };

  // ─── Generation ─────────────────────────────────────────
  const startGeneration = async () => {
    if (panels.length === 0) return;
    setIsGenerating(true);
    setResults([]);
    setError(null);
    setIsSavedInSession(false);
    setStep(AppStep.Results);

    try {
      const newResults: MockupResult[] = [];
      const activeScenario = SCENARIOS.find(s => s.id === scenarioId) || SCENARIOS[0];

      const vehicleForService: VehicleDetails = {
        brand:   branding.brand   || 'Unknown',
        model:   branding.model   || 'Unknown',
        year:    branding.year    || '',
        version: branding.style   || '',
        config:  instructions     || '',
      };

      const generationViews = [
        { ...VIEWS[0], labelPrefix: 'Generazione: 3/4 Anteriore' },
        { ...VIEWS[1], labelPrefix: 'Generazione: 3/4 Posteriore' },
        { ...VIEWS[2], labelPrefix: 'Generazione: Drone Top' },
      ];

      for (let i = 0; i < generationViews.length; i++) {
        const view = generationViews[i];
        setCurrentGenView(view.labelPrefix);
        const genResult = await generateCarMockup(
          view,
          panels,
          vehicleForService,
          activeScenario.prompt,
          carPhotos.map(p => ({ base64: p.base64, angle: p.angle })),
          newResults.map(r => ({
            viewName: r.viewName,
            base64: r.imageUrl.startsWith('data:') ? r.imageUrl.split(',')[1] : r.imageUrl,
          })),
        );
        const result: MockupResult = {
          id: Math.random().toString(36).substr(2, 9),
          viewName: view.label,
          imageUrl: genResult.imageUrl,
          storageUrl: genResult.storageUrl,
        };
        newResults.push(result);
        setResults([...newResults]);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Generation failed. Please try again.');
    } finally {
      setIsGenerating(false);
      setCurrentGenView('');
    }
  };

  // ─── Benchmark ──────────────────────────────────────────
  // ─── Gallery ────────────────────────────────────────────
  const saveToGallery = () => {
    const proj: SavedProject = {
      id:        Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      vehicle: {
        brand: branding.brand, model: branding.model,
        year: branding.year,   version: branding.style, config: '',
      },
      results: [...results],
    };
    const updated = [proj, ...gallery];
    setGallery(updated);
    localStorage.setItem('mockgen_gallery', JSON.stringify(updated));
    setIsSavedInSession(true);
  };

  const deleteFromGallery = (id: string) => {
    const updated = gallery.filter(p => p.id !== id);
    setGallery(updated);
    localStorage.setItem('mockgen_gallery', JSON.stringify(updated));
  };

  const resetApp = () => {
    setPanels([]);
    setCarPhotos([]);
    setInstructions('');
    setResults([]);
    setBranding({ brand: '', model: '', year: '', style: '' });
    setIsSavedInSession(false);
    setError(null);
    setStep(AppStep.Upload);
  };

  const getProgressStatus = () => {
    if (!isGenerating) return results.length === 3 ? 'All views ready.' : '';
    if (results.length === 0) return 'Generating front view\u2026';
    if (results.length === 1) return 'Front ready. Generating rear\u2026';
    if (results.length === 2) return 'Rear ready. Generating top view\u2026';
    return '';
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--fs-bg)', color: 'var(--fs-text)', fontFamily: 'var(--fs-font-body)' }}
    >
      <AppHeader
        onGalleryClick={() => setStep(step === AppStep.Gallery ? AppStep.Upload : AppStep.Gallery)}
        onNewMockup={resetApp}
        currentStep={step}
        onOpenAuth={openAuth}
      />

      <div style={{ height: 'var(--fs-header-height)' }} />

      <main
        className="flex-1 w-full mx-auto px-4 pb-24"
        style={{ maxWidth: 'var(--fs-max-width)', padding: '0 var(--fs-page-px-mobile)' }}
      >
        <StepIndicator currentStep={step} />

        {editingPanel && (
          <CropModal
            panel={editingPanel}
            onSave={(updated) => {
              setPanels(prev => prev.map(p => p.id === updated.id ? updated : p));
              setEditingPanel(null);
            }}
            onClose={() => setEditingPanel(null)}
          />
        )}

        {/* STEP 1: Upload Panels */}
        {step === AppStep.Upload && (
          <div className="space-y-8">
            <SectionHeading
              title="Upload Wrap Panels"
              subtitle="Upload your design panels. Each panel maps to a specific zone of the vehicle."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {MAIN_PANELS.map(type => (
                <PanelCard
                  key={type}
                  type={type}
                  panels={panels}
                  onFileUpload={handlePanelUpload}
                  onPaste={handlePanelPaste}
                  onUpdateType={(id, t) => setPanels(prev => prev.map(p => p.id === id ? { ...p, type: t } : p))}
                  onEdit={setEditingPanel}
                  onRemove={(id) => setPanels(prev => prev.filter(p => p.id !== id))}
                />
              ))}
            </div>
            <div className="flex flex-col items-center gap-5">
              <button onClick={() => setShowOptional(!showOptional)} className="fs-chip flex items-center gap-2">
                <span style={{ fontSize: 14 }}>{showOptional ? '\u2212' : '+'}</span>
                <span>Additional panels (optional)</span>
              </button>
              {showOptional && (
                <div className="w-full grid grid-cols-2 lg:grid-cols-4 gap-5">
                  {OPTIONAL_PANELS.map(type => (
                    <PanelCard
                      key={type}
                      type={type}
                      panels={panels}
                      onFileUpload={handlePanelUpload}
                      onPaste={handlePanelPaste}
                      onUpdateType={(id, t) => setPanels(prev => prev.map(p => p.id === id ? { ...p, type: t } : p))}
                      onEdit={setEditingPanel}
                      onRemove={(id) => setPanels(prev => prev.filter(p => p.id !== id))}
                    />
                  ))}
                </div>
              )}
            </div>
            {panels.length > 0 && (
              <div className="flex justify-center">
                <span className="fs-badge fs-badge--beta" style={{ fontSize: 11 }}>
                  {panels.length} / 7 panels loaded
                </span>
              </div>
            )}
            <NavActions
              onNext={() => setStep(AppStep.Vehicle)}
              nextLabel="Next: Car Photos &rarr;"
              nextDisabled={panels.length === 0}
            />
          </div>
        )}

        {/* STEP 2: Car Upload */}
        {step === AppStep.Vehicle && (
          <div className="space-y-8">
            <SectionHeading
              title="Upload Your Car"
              subtitle="Upload 1 to 3 photos. More angles help the AI reconstruct your vehicle's geometry accurately."
            />
            <div
              className="flex gap-3 items-start p-4"
              style={{
                background:   'var(--fs-cyan-dim)',
                border:       '1px solid rgba(0,212,255,0.2)',
                borderRadius: 'var(--fs-radius-card)',
              }}
            >
              <svg className="flex-shrink-0 mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--fs-cyan)" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 'var(--fs-text-sm)', color: 'var(--fs-text-muted)', lineHeight: 1.6 }}>
                For custom-bodied vehicles or highly modified cars, all 3 angles are recommended.
                Without correct views, the AI cannot accurately reconstruct non-standard surfaces.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {CAR_ANGLES.map(angle => (
                <CarPhotoSlot
                  key={angle}
                  angle={angle}
                  photo={carPhotos.find(p => p.angle === angle)}
                  onUpload={handleCarPhotoUpload}
                  onPaste={handleCarPhotoPaste}
                  onRemove={(a) => setCarPhotos(prev => prev.filter(p => p.angle !== a))}
                />
              ))}
            </div>
            <div className="space-y-2">
              <label
                className="block text-[10px] uppercase tracking-widest"
                style={{ color: 'var(--fs-text-disabled)', fontFamily: 'var(--fs-font-headline)' }}
              >
                Additional instructions <span style={{ color: 'var(--fs-text-disabled)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>(optional)</span>
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={3}
                placeholder="e.g. Focus on the front bumper integration, maintain the carbon fiber texture on the hood..."
                className="w-full outline-none resize-none transition-all"
                style={{
                  background:   'var(--fs-surface-low)',
                  border:       '1px solid var(--fs-border)',
                  borderRadius: 'var(--fs-radius-md)',
                  padding:      '12px 16px',
                  fontFamily:   'var(--fs-font-body)',
                  fontSize:     'var(--fs-text-sm)',
                  color:        'var(--fs-text)',
                  lineHeight:   1.6,
                }}
                onFocus={e => {
                  e.target.style.borderColor = 'rgba(0,212,255,0.4)';
                  e.target.style.boxShadow   = '0 0 0 3px rgba(0,212,255,0.08)';
                }}
                onBlur={e => {
                  e.target.style.borderColor = 'var(--fs-border)';
                  e.target.style.boxShadow   = 'none';
                }}
              />
            </div>
            <NavActions
              onBack={() => setStep(AppStep.Upload)}
              onNext={() => setStep(AppStep.Scenario)}
              nextLabel="Next: Scenario &rarr;"
              nextDisabled={carPhotos.length === 0}
            />
          </div>
        )}

        {/* STEP 3: Scenario */}
        {step === AppStep.Scenario && (
          <div className="space-y-8">
            <SectionHeading
              title="Choose a Scenario"
              subtitle="Select the environment where your vehicle will be visualized."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SCENARIOS.map(s => {
                const isSelected = scenarioId === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setScenarioId(s.id)}
                    className="group relative overflow-hidden text-left transition-all"
                    style={{
                      aspectRatio:  '16/10',
                      borderRadius: 'var(--fs-radius-card)',
                      border:       isSelected ? '2px solid var(--fs-cyan)' : '1px solid var(--fs-border)',
                      boxShadow:    isSelected ? 'var(--fs-glow-cyan)' : 'none',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--fs-border-soft)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--fs-border)'; }}
                  >
                    <img
                      src={s.imageUrl}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ opacity: isSelected ? 0.7 : 0.55 }}
                      alt={s.name}
                    />
                    <div
                      className="absolute inset-0 p-5 flex flex-col justify-end"
                      style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)' }}
                    >
                      {isSelected && (
                        <span className="fs-badge fs-badge--beta mb-2 self-start">Selected</span>
                      )}
                      <h3
                        style={{
                          fontFamily:    'var(--fs-font-headline)',
                          fontWeight:    'var(--fs-weight-bold)',
                          fontSize:      'var(--fs-text-lg)',
                          color:         'var(--fs-text)',
                          marginBottom:  4,
                          letterSpacing: 'var(--fs-tracking-tight)',
                        }}
                      >
                        {s.name}
                      </h3>
                      <p
                        style={{
                          fontFamily: 'var(--fs-font-body)',
                          fontSize:   'var(--fs-text-xs)',
                          color:      'var(--fs-text-muted)',
                          display:    '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {s.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            {error && (
              <div
                className="p-4 text-sm text-center"
                style={{
                  background:   'rgba(239,68,68,0.1)',
                  border:       '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 'var(--fs-radius-md)',
                  color:        '#EF4444',
                }}
              >
                {error}
              </div>
            )}
            <NavActions
              onBack={() => setStep(AppStep.Vehicle)}
              onNext={() => setStep(AppStep.Generation)}
              nextLabel="Next: Branding &rarr;"
            />
          </div>
        )}

        {/* STEP 4: Branding */}
        {step === AppStep.Generation && (
          <div className="space-y-8">
            <SectionHeading
              title="Branding"
              subtitle="Add your client's details and an optional logo. These appear in the mockup caption."
            />
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6"
              style={{
                background:   'var(--fs-surface)',
                border:       '1px solid var(--fs-border)',
                borderRadius: 'var(--fs-radius-card)',
              }}
            >
              {[
                { key: 'brand', label: 'Make', placeholder: 'BMW' },
                { key: 'model', label: 'Model', placeholder: 'M4 Competition' },
                { key: 'year', label: 'Year', placeholder: '2024' },
                { key: 'style', label: 'Style / Version', placeholder: 'Racing Livery' },
              ].map(({ key, label, placeholder }) => (
                <div key={key} className="space-y-2">
                  <label
                    className="block text-[10px] uppercase tracking-widest"
                    style={{ color: 'var(--fs-text-disabled)', fontFamily: 'var(--fs-font-headline)' }}
                  >
                    {label}
                  </label>
                  <input
                    type="text"
                    placeholder={placeholder}
                    value={(branding as any)[key]}
                    onChange={(e) => setBranding(p => ({ ...p, [key]: e.target.value }))}
                    className="w-full outline-none transition-all"
                    style={{
                      background:   'var(--fs-surface-low)',
                      border:       '1px solid var(--fs-border)',
                      borderRadius: 'var(--fs-radius-md)',
                      padding:      '11px 14px',
                      fontFamily:   'var(--fs-font-body)',
                      fontSize:     'var(--fs-text-sm)',
                      color:        'var(--fs-text)',
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = 'rgba(0,212,255,0.4)';
                      e.target.style.boxShadow   = '0 0 0 3px rgba(0,212,255,0.08)';
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = 'var(--fs-border)';
                      e.target.style.boxShadow   = 'none';
                    }}
                  />
                </div>
              ))}

              {/* Logo upload */}
              <div className="md:col-span-2 space-y-2">
                <label
                  className="block text-[10px] uppercase tracking-widest"
                  style={{ color: 'var(--fs-text-disabled)', fontFamily: 'var(--fs-font-headline)' }}
                >
                  Client Logo{' '}
                  <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>(optional)</span>
                </label>
                {branding.logoPreview ? (
                  <div className="flex items-center gap-4">
                    <div
                      className="w-20 h-12 flex items-center justify-center overflow-hidden"
                      style={{
                        background:   'var(--fs-surface-low)',
                        border:       '1px solid var(--fs-border)',
                        borderRadius: 'var(--fs-radius-md)',
                      }}
                    >
                      <img src={branding.logoPreview} className="max-w-full max-h-full object-contain" alt="Logo preview" />
                    </div>
                    <button
                      className="fs-btn-text text-xs"
                      onClick={() => setBranding(p => ({ ...p, logoPreview: undefined, logoBase64: undefined }))}
                    >
                      Remove logo
                    </button>
                  </div>
                ) : (
                  <label
                    className="flex items-center gap-3 p-4 cursor-pointer transition-all"
                    style={{
                      background:   'var(--fs-surface-low)',
                      border:       '1px dashed var(--fs-border)',
                      borderRadius: 'var(--fs-radius-md)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'var(--fs-cyan)';
                      e.currentTarget.style.background  = 'var(--fs-cyan-dim)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--fs-border)';
                      e.currentTarget.style.background  = 'var(--fs-surface-low)';
                    }}
                  >
                    <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--fs-text-disabled)" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span style={{ fontFamily: 'var(--fs-font-body)', fontSize: 'var(--fs-text-sm)', color: 'var(--fs-text-muted)' }}>
                      Click to upload logo (PNG with transparency recommended)
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Caption preview */}
            {(branding.brand || branding.model) && (
              <div
                className="flex items-center gap-3 p-3"
                style={{
                  background:   'var(--fs-surface)',
                  border:       '1px solid var(--fs-border)',
                  borderRadius: 'var(--fs-radius-md)',
                }}
              >
                <span style={{ fontFamily: 'var(--fs-font-body)', fontSize: 'var(--fs-text-xs)', color: 'var(--fs-text-disabled)' }}>
                  Caption preview:
                </span>
                <span
                  style={{
                    fontFamily: 'var(--fs-font-headline)',
                    fontSize:   'var(--fs-text-sm)',
                    fontWeight: 'var(--fs-weight-semi)',
                    color:      'var(--fs-text)',
                    letterSpacing: 'var(--fs-tracking-tight)',
                  }}
                >
                  {[branding.brand, branding.model, branding.year, branding.style].filter(Boolean).join(' \u00B7 ')}
                </span>
              </div>
            )}

            <NavActions
              onBack={() => setStep(AppStep.Scenario)}
              onNext={startGeneration}
              nextLabel="Generate Mockup"
              nextStyle={{ padding: '12px 36px', background: 'var(--fs-cyan)', letterSpacing: '0.08em' }}
            />
          </div>
        )}

        {/* STEP 5: Results */}
        {step === AppStep.Results && (
          <div className="space-y-12">
            {isGenerating ? (
              <div className="text-center space-y-5 pt-4">
                <h2
                  style={{
                    fontFamily:    'var(--fs-font-headline)',
                    fontWeight:    'var(--fs-weight-bold)',
                    fontSize:      'var(--fs-text-2xl)',
                    color:         'var(--fs-text)',
                    letterSpacing: 'var(--fs-tracking-tight)',
                  }}
                >
                  {currentGenView || 'Generating your mockup\u2026'}
                </h2>
                <div
                  className="w-64 mx-auto overflow-hidden"
                  style={{ height: 6, background: 'var(--fs-surface-mid)', borderRadius: 'var(--fs-radius-full)' }}
                >
                  <div
                    className="h-full transition-all duration-700 ease-out"
                    style={{
                      width:        `${((results.length + 0.5) / 3) * 100}%`,
                      background:   'var(--fs-cyan)',
                      borderRadius: 'var(--fs-radius-full)',
                      boxShadow:    '0 0 8px rgba(0,212,255,0.5)',
                    }}
                  />
                </div>
                <div className="flex items-center justify-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: 'var(--fs-cyan)', animation: 'fs-pulse 2s ease-in-out infinite' }}
                  />
                  <p
                    style={{
                      fontFamily:    'var(--fs-font-body)',
                      fontSize:      'var(--fs-text-xs)',
                      color:         'var(--fs-text-muted)',
                      letterSpacing: 'var(--fs-tracking-wide)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {getProgressStatus()}
                  </p>
                </div>
              </div>
            ) : results.length > 0 && (
              <div className="text-center space-y-3 pt-4">
                <h2
                  style={{
                    fontFamily:    'var(--fs-font-headline)',
                    fontWeight:    'var(--fs-weight-bold)',
                    fontSize:      'var(--fs-text-3xl)',
                    color:         'var(--fs-text)',
                    letterSpacing: 'var(--fs-tracking-tight)',
                  }}
                >
                  Your Mockup
                </h2>
                {(branding.brand || branding.model) && (
                  <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 'var(--fs-text-sm)', color: 'var(--fs-text-muted)' }}>
                    {[branding.brand, branding.model, branding.year, branding.style].filter(Boolean).join(' \u00B7 ')}
                  </p>
                )}
                <div className="flex justify-center pt-2">
                  <button
                    onClick={saveToGallery}
                    disabled={isSavedInSession || results.length < 3}
                    className="fs-badge"
                    style={{
                      cursor:     isSavedInSession || results.length < 3 ? 'default' : 'pointer',
                      background: isSavedInSession ? 'rgba(34,197,94,0.1)' : 'var(--fs-surface)',
                      color:      isSavedInSession ? 'var(--fs-stable)' : 'var(--fs-text-muted)',
                      border:     isSavedInSession ? '1px solid var(--fs-stable-border)' : '1px solid var(--fs-border)',
                      padding:    '5px 14px',
                      fontSize:   12,
                    }}
                  >
                    {isSavedInSession ? '\u2713 Saved to Gallery' : 'Save to Gallery'}
                  </button>
                </div>
              </div>
            )}

            {/* Result cards */}
            <div className="space-y-10">
              {VIEWS.map((view) => {
                const result = results.find(r => r.viewName === view.label);
                return result ? (
                  <div key={view.id} className="group space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                          style={{ background: 'var(--fs-cyan)', color: 'var(--fs-on-cyan)' }}
                        >
                          &#10003;
                        </span>
                        <span
                          style={{
                            fontFamily:    'var(--fs-font-body)',
                            fontSize:      'var(--fs-text-xs)',
                            color:         'var(--fs-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: 'var(--fs-tracking-wider)',
                          }}
                        >
                          {view.label}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          downloadWithWatermark(
                            result.imageUrl,
                            `mockgen-${(branding.brand || 'vehicle').toLowerCase()}-${view.id}.png`
                          )
                        }
                        className="fs-btn-ghost flex items-center gap-2"
                        style={{ padding: '6px 14px', fontSize: 'var(--fs-text-xs)' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </button>
                    </div>
                    <div
                      className="relative overflow-hidden"
                      style={{
                        borderRadius: 'var(--fs-radius-card)',
                        border:       '1px solid var(--fs-border)',
                        boxShadow:    'var(--fs-shadow-card)',
                      }}
                    >
                      <img src={result.imageUrl} className="w-full aspect-video object-cover" alt={view.label} />
                    </div>
                  </div>
                ) : (
                  <div key={view.id} className="space-y-3" style={{ opacity: 0.45 }}>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ border: '1px solid var(--fs-border)' }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--fs-text-disabled)', animation: 'fs-pulse 2s ease-in-out infinite' }} />
                      </div>
                      <span
                        style={{
                          fontFamily:    'var(--fs-font-body)',
                          fontSize:      'var(--fs-text-xs)',
                          color:         'var(--fs-text-disabled)',
                          textTransform: 'uppercase',
                          letterSpacing: 'var(--fs-tracking-wider)',
                        }}
                      >
                        {view.label}
                      </span>
                    </div>
                    <div
                      className="relative aspect-video overflow-hidden flex items-center justify-center"
                      style={{
                        background:   'var(--fs-surface)',
                        border:       '1px solid var(--fs-border)',
                        borderRadius: 'var(--fs-radius-card)',
                      }}
                    >
                      <div
                        className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
                        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent)' }}
                      />
                      <div className="flex flex-col items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
                          style={{ borderColor: 'var(--fs-border)', borderTopColor: 'var(--fs-cyan)' }}
                        />
                        <span
                          style={{
                            fontFamily:    'var(--fs-font-body)',
                            fontSize:      'var(--fs-text-xs)',
                            color:         'var(--fs-text-disabled)',
                            textTransform: 'uppercase',
                            letterSpacing: 'var(--fs-tracking-wider)',
                          }}
                        >
                          Generating&hellip;
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Download All */}
            {!isGenerating && results.length === VIEWS.length && (
              <div className="flex flex-col items-center gap-4 pt-8" style={{ borderTop: '1px solid var(--fs-border)' }}>
                <button
                  className="fs-btn-primary"
                  style={{ padding: '12px 32px' }}
                  onClick={() => {
                    results.forEach((r, i) => {
                      setTimeout(() => {
                        downloadWithWatermark(
                          r.imageUrl,
                          `mockgen-${(branding.brand || 'vehicle').toLowerCase()}-${VIEWS[i]?.id || i}.png`
                        );
                      }, i * 300);
                    });
                  }}
                >
                  Download All {VIEWS.length} Views
                </button>
                <button className="fs-btn-ghost" style={{ padding: '10px 28px' }} onClick={resetApp}>
                  New Mockup
                </button>
              </div>
            )}
          </div>
        )}

        {/* GALLERY */}
        {step === AppStep.Gallery && (
          <div className="space-y-8">
            <SectionHeading title="Your Gallery" subtitle="Mockups saved locally in this browser." />
            {gallery.length === 0 ? (
              <div
                className="py-20 text-center space-y-4"
                style={{
                  background:   'var(--fs-surface)',
                  border:       '1px solid var(--fs-border)',
                  borderRadius: 'var(--fs-radius-card)',
                }}
              >
                <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 'var(--fs-text-sm)', color: 'var(--fs-text-disabled)', fontStyle: 'italic' }}>
                  No saved projects yet.
                </p>
                <button className="fs-btn-text text-xs uppercase tracking-widest" onClick={resetApp}>
                  Start a new mockup
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gallery.map(proj => (
                  <div
                    key={proj.id}
                    className="group overflow-hidden transition-all"
                    style={{
                      background:   'var(--fs-surface)',
                      border:       '1px solid var(--fs-border)',
                      borderRadius: 'var(--fs-radius-card)',
                    }}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={proj.results[0]?.imageUrl}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        alt="Preview"
                      />
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        style={{ background: 'rgba(0,0,0,0.5)' }}
                      >
                        <button
                          onClick={() => {
                            setBranding({
                              brand: proj.vehicle.brand,
                              model: proj.vehicle.model,
                              year:  proj.vehicle.year,
                              style: proj.vehicle.version,
                            });
                            setResults(proj.results);
                            setStep(AppStep.Results);
                          }}
                          className="fs-btn-primary"
                          style={{ padding: '8px 20px', fontSize: 'var(--fs-text-xs)' }}
                        >
                          View Results
                        </button>
                      </div>
                    </div>
                    <div className="p-4 flex justify-between items-center">
                      <div>
                        <h4
                          style={{
                            fontFamily:    'var(--fs-font-headline)',
                            fontWeight:    'var(--fs-weight-semi)',
                            fontSize:      'var(--fs-text-sm)',
                            color:         'var(--fs-text)',
                            letterSpacing: 'var(--fs-tracking-tight)',
                          }}
                        >
                          {proj.vehicle.brand} {proj.vehicle.model}
                        </h4>
                        <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 'var(--fs-text-xs)', color: 'var(--fs-text-disabled)', marginTop: 2 }}>
                          {new Date(proj.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteFromGallery(proj.id)}
                        style={{ color: 'var(--fs-text-disabled)', padding: 6 }}
                        className="transition-colors text-sm"
                        onMouseEnter={e => (e.currentTarget.style.color = '#EF4444')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--fs-text-disabled)')}
                      >
                        &#10005;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-center pt-4">
              <button className="fs-btn-text text-xs uppercase tracking-widest" onClick={resetApp}>
                &larr; Back to App
              </button>
            </div>
          </div>
        )}

        {/* Mobile persistent CTA */}
        {step === AppStep.Upload && panels.length > 0 && (
          <div
            className="fixed bottom-0 inset-x-0 p-4 pointer-events-none md:hidden z-50"
            style={{ background: 'linear-gradient(to top, var(--fs-bg), transparent)' }}
          >
            <button
              onClick={() => setStep(AppStep.Vehicle)}
              className="fs-btn-primary w-full pointer-events-auto"
              style={{ padding: '14px', fontSize: 'var(--fs-text-sm)' }}
            >
              Continue &rarr;
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="fs-app-footer">
        <div className="fs-app-footer__inner">
          <div className="fs-app-footer__left">
            <img
              src={logoFormaStudio}
              alt="FORMA Studio"
              className="fs-app-footer__logo"
              style={{ height: 20, width: 'auto' }}
            />
            <span className="fs-app-footer__copy">&copy; 2026 Frank Ricci Design &mdash; FORMA Studio</span>
          </div>
          <nav className="fs-app-footer__links">
            <a href="/privacy" className="fs-app-footer__link">Privacy</a>
            <a href="/terms"   className="fs-app-footer__link">Terms</a>
            <a href="mailto:frankriccidesign@gmail.com" className="fs-app-footer__link">Contact</a>
            <a href="https://forma.frankriccidesign.com" className="fs-app-footer__link fs-app-footer__link--portal">
              &larr; Back to Portal
            </a>
          </nav>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuth} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// AUTH GATE — shows lockscreen if not authenticated
// ════════════════════════════════════════════════════════════
function AuthGate() {
  const { session, isLoading } = useAuth();

  if (isLoading) return null;
  if (!session) return <LockScreen />;

  return <MockGenContent />;
}

// ════════════════════════════════════════════════════════════
// ROOT EXPORT — wraps with AuthProvider
// ════════════════════════════════════════════════════════════
export const App: React.FC = () => (
  <AuthProvider>
    <AuthGate />
  </AuthProvider>
);
